import React, { useState, useMemo, useEffect } from "react";
import {
  useCategories,
  useBrands,
  usePaginatedProducts,
} from "@/hooks/useProducts.js";
import { useMedRecFilter } from "@/context/MedRecFilterContext.jsx";
import { Link } from "react-router-dom";
import { useCart } from "@/hooks/useCart.js";
import { useAuth } from "@/hooks/useAuth.js";
import { formatCurrency } from "@/utils/formatters.js";
import { ProductQuantityCalculator } from "@/utils/ProductQuantityCalculator.js";
import { isBelowOneCase } from "@/utils/quantityHelpers.js";
import QuantityInput from "@/components/cart/QuantityInput.jsx";
import LoadingSpinner from "@/components/common/LoadingSpinner.jsx";
import { useBackOrder } from "@/hooks/useBackOrder.js";
import BackOrderAdjustModal from "@/components/backorder/BackOrderAdjustModal.jsx";

// Cannabis icon for Med/Rec badges
import cannabisIcon from "@/assets/icons/reccannabis.png";

const QuickOrderPage = () => {
  const [browseMode, setBrowseMode] = useState("category"); // 'category' or 'brand'
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");

  // Get Med/Rec filter from global context
  const { filterValues, showFilter: showMedRecBadge } = useMedRecFilter();

  const { canProceedToShopping } = useAuth();
  const {
    updateProductQuantity,
    getItemQuantityInCart,
    isUpdatingProductQuantity,
    getCartItemCount,
    getCartTotal,
  } = useCart();

  // Fetch categories and brands
  const { data: categories = [], isLoading: categoriesLoading } = useCategories(
    {
      enabled: browseMode === "category",
    },
  );
  const { data: brands = [], isLoading: brandsLoading } = useBrands({
    enabled: browseMode === "brand",
  });

  // Auto-select first category/brand when data loads
  useEffect(() => {
    if (selectedId === null && !globalSearchTerm) {
      if (browseMode === "category" && categories.length > 0) {
        setSelectedId(categories[0].id);
      } else if (browseMode === "brand" && brands.length > 0) {
        setSelectedId(brands[0].id);
      }
    }
  }, [browseMode, categories, brands, selectedId, globalSearchTerm]);

  // Fetch products based on selection
  const productQueryOptions = useMemo(() => {
    const options = {
      pageSize: 100, // Load more products for quick ordering
      inStock: true, // Only show in-stock products
      sortBy: "productName",
      sortDirection: "asc",
      isMed: filterValues.isMed,
      isRec: filterValues.isRec,
    };

    // If there's a global search, don't filter by category/brand selection
    if (globalSearchTerm.trim()) {
      options.search = globalSearchTerm.trim();
      // Don't apply category/brand filters when doing global search
    } else {
      // Apply category/brand filters only when not doing global search
      if (browseMode === "category" && selectedId) {
        options.categoryId = selectedId;
      } else if (browseMode === "brand" && selectedId) {
        options.brandId = selectedId;
      }
    }

    return options;
  }, [browseMode, selectedId, globalSearchTerm, filterValues.isMed, filterValues.isRec]);

  const {
    products,
    isLoading: productsLoading,
    error: productsError,
  } = usePaginatedProducts(productQueryOptions);

  // Get current selection info
  const selectedItem = useMemo(() => {
    if (browseMode === "category" && selectedId) {
      return categories.find((cat) => cat.id === selectedId);
    } else if (browseMode === "brand" && selectedId) {
      return brands.find((brand) => brand.id === selectedId);
    }
    return null;
  }, [browseMode, selectedId, categories, brands]);

  const handleModeChange = (newMode) => {
    setBrowseMode(newMode);
    setSelectedId(null);
    setSearchTerm("");
    setGlobalSearchTerm(""); // Clear global search when switching modes
  };

  const handleSelectionChange = (id) => {
    setSelectedId(id);
    setSearchTerm(""); // Clear sidebar search when selecting from list
    setGlobalSearchTerm(""); // Clear global search when selecting category/brand
  };

  const handleGlobalSearchChange = (value) => {
    setGlobalSearchTerm(value);
    if (value.trim()) {
      // Clear selection when doing global search to show all matching products
      setSelectedId(null);
    }
  };

  const handleQuantityUpdate = async (product, newQuantity) => {
    try {
      await updateProductQuantity(product.id, newQuantity, {
        unitPrice: (product.discountUnitPrice && product.discountUnitPrice > 0) ? product.discountUnitPrice : product.unitPrice,
        singleUnitFee: product.singleUnitFee,
        productSkuVariant: {
          ...product,
          // FIXED: Ensure both image formats are available for CartOffcanvas compatibility
          images: product.images || [], // Processed format (from productService)
          productSKUVariantImages: product.images
            ? product.images.map((img) => ({
                image: {
                  fullPath: img.fullPath.replace(
                    "https://stashhouseimages.blob.core.windows.net/",
                    "",
                  ),
                  isPrimary: img.isPrimary,
                },
                listOrder: img.displayOrder,
              }))
            : [], // Convert to raw format for database compatibility
          // Ensure all required fields are present
          name: product.name,
          flavor: product.flavor,
          brand: product.brandName ? { name: product.brandName } : null,
          unitsPerCase: product.unitsPerCase,
          optSellByTypeID: product.optSellByTypeID,
          unitAvailable: product.unitAvailable,
          unitPrice: product.unitPrice,
          singleUnitFee: product.singleUnitFee,
          description: product.description,
        },
      });
    } catch (error) {
      console.error("Failed to update product quantity:", error);
    }
  };

  // Don't render if user can't shop
  if (!canProceedToShopping()) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          <h4>Store Selection Required</h4>
          <p>Please select a store from your account to use Quick Order.</p>
        </div>
      </div>
    );
  }

  const sidebarItems = browseMode === "category" ? categories : brands;
  const isLoadingSidebar =
    browseMode === "category" ? categoriesLoading : brandsLoading;

  return (
    <div className="quick-order-page py-5">
      <section className="page-content">
        <div className="container-fluid ">
          <div className="row">
            {/* Sidebar */}
            <div className="col-lg-3 col-md-4">
              <div className="quick-order__sidebar">
                <section className="page-header pb-4 border-bottom  mb-4">
                
                    <nav aria-label="breadcrumb" className="mb-3">
                      <ol className="breadcrumb mb-0">
                        <li className="breadcrumb-item">
                          <Link to="/" className="text-decoration-none">
                            Home
                          </Link>
                        </li>

                        <li
                          className="breadcrumb-item active"
                          aria-current="page"
                        >
                          Quick Order
                        </li>
                      </ol>
                    </nav>
                    <div className="row">
                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h1 className="h2 quick-order__title">
                              Quick Order
                            </h1>
                            <p className="text-muted mb-0">
                              Quickly find and add products to your cart
                            </p>
                          </div>

                          {/* Cart Summary */}
                          <div className="quick-order__cart-summary d-none">
                            <div className="card">
                              <div className="card-body text-center">
                                <div className="fw-bold text-stash-accent fs-5">
                                  {formatCurrency(getCartTotal())}
                                </div>
                                <small className="text-muted">
                                  {getCartItemCount()} items in cart
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  
                </section>
                {/* Browse Mode Toggle */}
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 className="card-title">Browse By</h6>
                    <div className="btn-group w-100" role="group">
                      <button
                        type="button"
                        className={`btn ${browseMode === "category" ? "btn-stash-accent" : "btn-outline-stash-accent"}`}
                        onClick={() => handleModeChange("category")}
                      >
                        <i className="bi bi-grid me-1"></i>
                        Category
                      </button>
                      <button
                        type="button"
                        className={`btn ${browseMode === "brand" ? "btn-stash-accent" : "btn-outline-stash-accent"}`}
                        onClick={() => handleModeChange("brand")}
                      >
                        <i className="bi bi-tags me-1"></i>
                        Brand
                      </button>
                    </div>
                  </div>
                </div>

                {/* Global Product Search */}
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 className="card-title">
                      <i className="bi bi-search me-2"></i>
                      Search All Products
                    </h6>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by product name, flavor, brand, or category..."
                        value={globalSearchTerm}
                        onChange={(e) =>
                          handleGlobalSearchChange(e.target.value)
                        }
                      />
                      {globalSearchTerm && (
                        <button
                          className="btn btn-outline-stash-accent"
                          type="button"
                          onClick={() => handleGlobalSearchChange("")}
                          title="Clear search"
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      )}
                      <button className="btn btn-stash-accent" type="button">
                        <i className="bi bi-search"></i>
                      </button>
                    </div>
                    {globalSearchTerm && (
                      <small className="text-muted mt-1 d-block">
                        Searching across all products, brands, and categories
                      </small>
                    )}
                  </div>
                </div>

                {/* Search */}
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 className="card-title">
                      <i className="bi bi-search me-2"></i>
                      Search{" "}
                      {browseMode === "category" ? "Categories" : "Brands"}
                    </h6>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder={`Filter ${browseMode === "category" ? "categories" : "brands"}...`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <button
                          className="btn btn-outline-stash-accent"
                          type="button"
                          onClick={() => setSearchTerm("")}
                          title="Clear filter"
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      )}
                      <button className="btn btn-stash-accent" type="button">
                        <i className="bi bi-funnel"></i>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Categories/Brands List */}
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">
                      {browseMode === "category" ? "Categories" : "Brands"}
                    </h6>
                  </div>
                  <div className="card-body p-0">
                    {isLoadingSidebar ? (
                      <div className="text-center py-3">
                        <LoadingSpinner size="sm" />
                      </div>
                    ) : (
                      <div className="list-group list-group-flush">
                        {sidebarItems
                          .filter(
                            (item) =>
                              !searchTerm ||
                              item.name
                                .toLowerCase()
                                .includes(searchTerm.toLowerCase()),
                          )
                          .map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                                selectedId === item.id
                                  ? "bg-stash-accent text-white"
                                  : ""
                              }`}
                              onClick={() => handleSelectionChange(item.id)}
                            >
                              <div className="d-flex align-items-center">
                                <span>{item.name}</span>
                              </div>
                              {selectedId === item.id && (
                                <i className="bi bi-chevron-right"></i>
                              )}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="col-lg-9 col-md-8">
              <div className="quick-order__main">
                {selectedItem && (
                  <div className="card mb-3">
                    <div className="card-body bg-stash-gray-900 ">
                      <div className="d-flex align-items-center flex-grow-1 justify-content-center">
                        <div className="text-center">
                          <h3 className="mb-0 text-white">
                            {selectedItem.name}
                          </h3>
                          <small className="text-white">
                            {products.length} product
                            {products.length !== 1 ? "s" : ""} available
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Product Search */}
                {selectedId && !globalSearchTerm && (
                  <div className="card mb-3 d-none">
                    <div className="card-body">
                      <div className="input-group input-group-lg">
                        <input
                          type="text"
                          className="form-control"
                          placeholder={`Search products in ${selectedItem?.name}...`}
                          value={globalSearchTerm}
                          onChange={(e) =>
                            handleGlobalSearchChange(e.target.value)
                          }
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                        >
                          <i className="bi bi-search"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Products List */}
                {!selectedId && !globalSearchTerm ? (
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <i
                        className="bi bi-arrow-left text-muted"
                        style={{ fontSize: "3rem" }}
                      ></i>
                      <h4 className="mt-3 text-muted">
                        Select a {browseMode} or Search
                      </h4>
                      <p className="text-muted">
                        Choose a {browseMode} from the sidebar or use the global
                        search to find products
                      </p>
                    </div>
                  </div>
                ) : productsLoading ? (
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <LoadingSpinner />
                      <p className="mt-3 text-muted">Loading products...</p>
                    </div>
                  </div>
                ) : productsError ? (
                  <div className="card">
                    <div className="card-body">
                      <div className="alert alert-danger">
                        <h4>Error Loading Products</h4>
                        <p>
                          {productsError.message || "Failed to load products"}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : products.length === 0 ? (
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <i
                        className="bi bi-box-seam text-muted"
                        style={{ fontSize: "3rem" }}
                      ></i>
                      <h4 className="mt-3 text-muted">No Products Found</h4>
                      <p className="text-muted">
                        {globalSearchTerm
                          ? `No products match "${globalSearchTerm}"`
                          : `No products available in ${selectedItem?.name || "this selection"}`}
                      </p>
                      {globalSearchTerm && (
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => handleGlobalSearchChange("")}
                        >
                          Clear Search
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="card">
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0 quickorder-table">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: "40px" }}></th>
                              <th></th>
                              <th style={{ width: "100px" }}></th>

                              <th
                                className="d-none"
                                style={{ width: "100px" }}
                              ></th>
                              <th className="" style={{ width: "200px" }}></th>
                              <th style={{ width: "160px" }}></th>
                              <th
                                className="text-end"
                                style={{ width: "160px" }}
                              ></th>
                            </tr>
                          </thead>
                          <tbody>
                            {products.map((product) => (
                              <ProductRow
                                key={product.id}
                                product={product}
                                cartQuantity={getItemQuantityInCart(product.id)}
                                onQuantityChange={(newQuantity) =>
                                  handleQuantityUpdate(product, newQuantity)
                                }
                                isUpdating={isUpdatingProductQuantity}
                                showMedRecBadge={showMedRecBadge}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ProductRow Component
const ProductRow = ({
  product,
  cartQuantity,
  onQuantityChange,
  isUpdating,
  showMedRecBadge,
}) => {
  const calculator = useMemo(() => {
    return new ProductQuantityCalculator(product);
  }, [product.id, product.unitsPerCase, product.optSellByTypeID]);

  const displayQuantity = useMemo(() => {
    return calculator.unitsToDisplay(cartQuantity);
  }, [calculator, cartQuantity]);

  const priceInfo = useMemo(() => {
    return calculator.calculatePrice(cartQuantity);
  }, [calculator, cartQuantity]);

  const { getItemBackOrderQuantity, canUseBackOrder } = useBackOrder();
  const [showBackOrderModal, setShowBackOrderModal] = useState(false);
  const currentBackOrderQty = getItemBackOrderQuantity(product.id);
  // Only offer a back order once less than a full case is left to buy (available minus cart).
  const canCreateBackOrder = product.allowBackorder && isBelowOneCase(product, cartQuantity);
  const showBackOrderButton = canUseBackOrder && (canCreateBackOrder || currentBackOrderQty > 0);

  const getProductImageUrl = (product) => {
    // Try processed images first (from productService)
    if (product.images && product.images.length > 0) {
      return product.images[0].fullPath;
    }

    // Try direct imageUrl property
    if (product.imageUrl) {
      return product.imageUrl;
    }

    // Fallback to placeholder
    return null;
  };

  const handleCaseChange = (newCases) => {
    const newTotal = calculator.displayToUnits(newCases, displayQuantity.units);
    onQuantityChange(newTotal);
  };

  const handleUnitChange = (newUnits) => {
    const newTotal = calculator.displayToUnits(displayQuantity.cases, newUnits);
    onQuantityChange(newTotal);
  };

  const quickAdd = () => {
    if (cartQuantity === 0) {
      if (calculator.canSellByCase && product.unitAvailable >= product.unitsPerCase) {
        onQuantityChange(calculator.unitsPerCase); // Add 1 case
      } else if (calculator.canSellByUnit) {
        onQuantityChange(1); // Add 1 unit
      }
    } else {
      const increment = calculator.smartIncrement(cartQuantity);
      onQuantityChange(increment);
    }
  };

  return (
    <tr className={cartQuantity > 0 ? "" : ""}>
      <td>
        <img
          src={getProductImageUrl(product)}
          alt={product.name}
          className="rounded"
          style={{ width: "120px", height: "120px", objectFit: "cover" }}
        />
      </td>
      <td>
        <div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <strong>{product.name}</strong>
            {/* Med/Rec badge - only show when store supports both types */}
            {showMedRecBadge && (product.isMed || product.isRec) && (
              <span
                className={`badge rounded-pill d-inline-flex align-items-center ${
                  product.isMed ? 'bg-success' : 'bg-purple'
                }`}
                title={product.isMed ? 'Medical Product' : 'Recreational Product'}
                style={{ fontSize: '0.65rem' }}
              >
                <img src={cannabisIcon} alt="" className="med-rec-badge-icon me-1" />
                {product.isMed ? 'Med' : 'Rec'}
              </span>
            )}
          </div>

          <div className="text-muted">{product.flavor}</div>

          <small className="text-muted">
            <strong>Brand: </strong>
            {product.brandName}
            <br />
            <strong>{calculator.unitLabelPlural}/{calculator.caseLabel}: </strong>
            {product.unitsPerCase} {calculator.unitLabelPlural.toLowerCase()}
            {calculator.canSellByUnit && ` • Individual ${calculator.unitLabelPlural.toLowerCase()} available`}
          </small>
        </div>
      </td>
      <td>
        {calculator.canSellByUnit && (
        <div
          className="d-flex align-items-center justify-content-end"
          style={{ height: "42px" }}
        >
          <small>Single {calculator.unitLabelPlural}</small>
        </div>
        )}
        {calculator.canSellByCase && (
        <div
          className="d-flex align-items-center justify-content-end"
          style={{ height: "42px" }}
        >
          <small>{calculator.caseLabelPlural}</small>
        </div>
        )}
      </td>

      <td className="d-none">
        <span
          className={`badge ${
            product.isInStock ? "bg-stash-accent" : "bg-danger"
          }`}
        >
          {product.isInStock
            ? `${product.unitAvailable} avail`
            : "Out of Stock"}
        </span>
      </td>
      <td>
        {product.isInStock ? (
          <div className="d-flex flex-column gap-1">
            {calculator.canSellByUnit && product.unitsPerCase > 1 && (
              <div className="d-flex align-items-center gap-1">
                <QuantityInput
                  value={displayQuantity.units}
                  onChange={handleUnitChange}
                  quantityType="units"
                  product={product}
                  currentCaseQuantity={displayQuantity.cases}
                  size="sm"
                  disabled={isUpdating}
                  isUpdating={isUpdating}
                />
              </div>
            )}
            {calculator.canSellByCase && (
            <div className="d-flex align-items-center gap-1">
              <QuantityInput
                value={displayQuantity.cases}
                onChange={handleCaseChange}
                quantityType="cases"
                product={product}
                currentUnitQuantity={displayQuantity.units}
                size="sm"
                disabled={isUpdating}
                isUpdating={isUpdating}
              />
            </div>
            )}
            {showBackOrderButton && (
              <button
                type="button"
                className="btn btn-outline-dark btn-sm mt-1"
                onClick={() => setShowBackOrderModal(true)}
              >
                <i className="bi bi-clock-history me-1"></i>
                {currentBackOrderQty > 0 ? "View Back Order" : "Create Back Order"}
              </button>
            )}
          </div>
        ) : product.allowBackorder ? (
          <button
            type="button"
            className="btn btn-outline-dark btn-sm"
            onClick={() => setShowBackOrderModal(true)}
          >
            <i className="bi bi-clock-history me-1"></i>
            {currentBackOrderQty > 0 ? "View Back Order" : "Create Back Order"}
          </button>
        ) : (
          <span className="text-muted">—</span>
        )}
        <BackOrderAdjustModal
          show={showBackOrderModal}
          onHide={() => setShowBackOrderModal(false)}
          product={product}
        />
      </td>
      <td>
        <div>
          {calculator.canSellByUnit && (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ height: "42px" }}
            >
              {calculator.hasDiscountPrice() && (
                <small className="text-decoration-line-through text-muted me-1">
                  {formatCurrency(product.unitPrice + (product.singleUnitFee || 0))}
                </small>
              )}
              <strong className="text-stash-accent">
                {formatCurrency(
                  priceInfo.unitPrice + (product.singleUnitFee || 0),
                )}
              </strong>
              <small className="text-muted">/{calculator.unitLabel.toLowerCase()}</small>
            </div>
          )}
          {calculator.canSellByCase && (
          <div
            className="d-flex align-items-center justify-content-center flex-grow"
            style={{ height: "42px" }}
          >
            {calculator.hasDiscountPrice() && (
              <small className="text-decoration-line-through text-muted me-1">
                {formatCurrency(product.unitPrice * product.unitsPerCase)}
              </small>
            )}
            <strong className="text-stash-accent">
              {formatCurrency(priceInfo.unitPrice * product.unitsPerCase)}
            </strong>
            <small className="text-muted d-block">/{calculator.caseLabel.toLowerCase()}</small>
          </div>
          )}
          {calculator.hasMOQPricing() && (
            <div className="text-center">
              <span className="badge bg-success" style={{fontSize: '0.6rem'}}>
                Volume Pricing
              </span>
            </div>
          )}
        </div>
      </td>
      <td>
        {cartQuantity > 0 ? (
          <div className="text-end">
            <div
              className="d-flex justify-content-end align-items-center"
              style={{ height: "42px" }}
            >
              {priceInfo.hasFeesPerUnit && (
                <small className="text-muted d-block px-3">
                  incl. {formatCurrency(priceInfo.fees)} fees
                </small>
              )}
            </div>
            <div
              className="d-flex border border-success justify-content-end align-items-center bg-white"
              style={{ height: "38px" }}
            >
              <strong className="text-stash-accent px-3">
                {formatCurrency(priceInfo.grandTotal)}
              </strong>
            </div>
            {priceInfo.hasSavings && (
              <small className="text-success d-block">
                Save {priceInfo.savingsPercent}%
              </small>
            )}
          </div>
        ) : (
          <span className="text-muted"></span>
        )}
      </td>
    </tr>
  );
};

export default QuickOrderPage;
